import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { getInitials, compressImage } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function EditProfilePage() {
  const { user, token, language, updateProfile } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '');
      setBio(user.bio || '');
      setAvatar(user.avatar || '');
    }
  }, [user]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file, 400, 0.8);
      setAvatar(compressed);
    } catch (error) {
      toast.error('Error processing photo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile({
        display_name: displayName,
        bio,
        avatar
      });
      toast.success(tr('saved'));
      navigate(`/profile/${user.username}`);
    } catch (error) {
      toast.error(tr('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="edit-profile-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-bold text-lg">{tr('editProfile')}</h1>
          </div>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="rounded-full"
            data-testid="save-profile-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : tr('save')}
          </Button>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {/* Avatar */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <Avatar className="w-28 h-28 ring-4 ring-primary/20">
              <AvatarImage src={avatar} alt={displayName} />
              <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                {getInitials(displayName || user?.username)}
              </AvatarFallback>
            </Avatar>
            <label className="absolute bottom-0 right-0 w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors">
              <Camera className="w-5 h-5" />
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleAvatarChange}
              />
            </label>
          </div>
        </div>

        {/* Display Name */}
        <div className="space-y-2">
          <Label htmlFor="displayName">{tr('displayName')}</Label>
          <Input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Non ou"
            className="h-12 rounded-2xl bg-secondary/30"
            data-testid="display-name-input"
          />
        </div>

        {/* Bio */}
        <div className="space-y-2">
          <Label htmlFor="bio">{tr('bio')}</Label>
          <Textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Pale de ou menm..."
            className="min-h-[100px] rounded-2xl bg-secondary/30 resize-none"
            maxLength={160}
            data-testid="bio-input"
          />
          <p className="text-sm text-muted-foreground text-right">{bio.length}/160</p>
        </div>
      </form>
    </div>
  );
}
